import { axiosClient } from '../axiosClient';
import type { OrcamentoDraftData } from '@/pages/Orcamentos/draftStore';

export type Categoria = 'leitor' | 'brinco' | 'aplicador' | 'acessorio';
export type Origem = 'alibaba' | 'proprio' | 'nacional';
export type MoedaCusto = 'USD' | 'BRL';

export const CATEGORIAS: { value: Categoria; label: string }[] = [
  { value: 'leitor', label: 'Leitor' },
  { value: 'brinco', label: 'Brinco RFID' },
  { value: 'aplicador', label: 'Aplicador' },
  { value: 'acessorio', label: 'Acessório' },
];

export interface Produto {
  id: number;
  categoria: Categoria;
  nome: string;
  modelo: string;
  origem: Origem;
  fornecedor: string;
  moedaCusto: MoedaCusto;
  custoUsd: number;
  custoBrl: number;
  pesoG: number;
  comprimentoCm: number;
  larguraCm: number;
  alturaCm: number;
  specs: string[];
  imagemUrl: string;
  ativo: boolean;
  ordem: number;
}

export type ProdutoInput = Omit<Produto, 'id'>;

export interface FreteOpcao {
  id: number | string;
  nome: string;
  empresa: string;
  preco: number;
  prazoDias: number | null;
}

export interface FreteResult {
  configurado: boolean;
  cepOrigem: string;
  opcoes: FreteOpcao[];
}

export interface FreteItem {
  id?: number;
  peso_g: number;
  comprimento_cm: number;
  largura_cm: number;
  altura_cm: number;
  quantidade: number;
  valor_seguro?: number;
}

export const orcamentosApi = {
  listProdutos: async (): Promise<Produto[]> => {
    const res = await axiosClient.get<{ produtos: Produto[] }>('/manager/orcamentos/produtos');
    return res.data.produtos ?? [];
  },

  createProduto: async (data: ProdutoInput): Promise<Produto> => {
    const res = await axiosClient.post<{ produto: Produto }>('/manager/orcamentos/produtos', data);
    return res.data.produto;
  },

  updateProduto: async (id: number, data: Partial<ProdutoInput>): Promise<Produto> => {
    const res = await axiosClient.put<{ produto: Produto }>(
      `/manager/orcamentos/produtos/${id}`,
      data
    );
    return res.data.produto;
  },

  deleteProduto: async (id: number): Promise<void> => {
    await axiosClient.delete(`/manager/orcamentos/produtos/${id}`);
  },

  cotarFrete: async (
    cepDestino: string,
    itens: FreteItem[],
    cepOrigem?: string
  ): Promise<FreteResult> => {
    const res = await axiosClient.post<FreteResult>('/manager/orcamentos/frete/cotar', {
      cepDestino,
      cepOrigem,
      itens,
    });
    return res.data;
  },

  // ----- Orçamentos salvos (histórico) -----
  listSalvos: async (): Promise<OrcamentoSalvoResumo[]> => {
    const res = await axiosClient.get<{ orcamentos: OrcamentoSalvoResumo[] }>(
      '/manager/orcamentos/salvos'
    );
    return res.data.orcamentos ?? [];
  },

  getSalvo: async (id: number): Promise<OrcamentoSalvo> => {
    const res = await axiosClient.get<{ orcamento: OrcamentoSalvo }>(
      `/manager/orcamentos/salvos/${id}`
    );
    return res.data.orcamento;
  },

  createSalvo: async (data: OrcamentoSalvoInput): Promise<OrcamentoSalvo> => {
    const res = await axiosClient.post<{ orcamento: OrcamentoSalvo }>(
      '/manager/orcamentos/salvos',
      data
    );
    return res.data.orcamento;
  },

  updateSalvo: async (id: number, data: OrcamentoSalvoInput): Promise<OrcamentoSalvo> => {
    const res = await axiosClient.put<{ orcamento: OrcamentoSalvo }>(
      `/manager/orcamentos/salvos/${id}`,
      data
    );
    return res.data.orcamento;
  },

  deleteSalvo: async (id: number): Promise<void> => {
    await axiosClient.delete(`/manager/orcamentos/salvos/${id}`);
  },
};

export type OrcamentoStatus = 'rascunho' | 'enviado' | 'vendido';

export interface OrcamentoSalvoResumo {
  id: number;
  clienteNome: string;
  clienteCidade: string;
  clienteUf: string;
  totalGeral: number;
  status: OrcamentoStatus;
  updatedAt: string;
  createdAt: string;
}

export interface OrcamentoSalvo extends OrcamentoSalvoResumo {
  dados: OrcamentoDraftData;
}

export interface OrcamentoSalvoInput {
  clienteNome: string;
  clienteCidade: string;
  clienteUf: string;
  totalGeral: number;
  status?: OrcamentoStatus;
  dados: OrcamentoDraftData;
}

// Câmbio USD→BRL ao vivo — API pública gratuita (AwesomeAPI), sem token e com CORS.
// Chamada direta (não passa pelo axiosClient do painel, que injeta Bearer/baseURL).
export async function fetchCambioUsdBrl(): Promise<number> {
  const res = await fetch('https://economia.awesomeapi.com.br/last/USD-BRL');
  if (!res.ok) throw new Error('Falha ao consultar câmbio');
  const data = await res.json();
  const bid = Number(data?.USDBRL?.bid);
  if (!bid || Number.isNaN(bid)) throw new Error('Cotação inválida');
  return bid;
}
